import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatDate, TICKET_STATUSES } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

function ticketBadge(status) {
  const map = { OPEN: 'warning', IN_PROGRESS: 'info', CLOSED: 'secondary' };
  const c = map[status] || 'secondary';
  return <span className={`badge text-bg-${c}`}>{status || '—'}</span>;
}

export default function SupportTickets() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      if (status) params.status = status;
      const raw = await adminApi.listSupportTickets(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, status]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'subject',
      label: 'Subject',
      render: (r) => r.subject || r.title || r.ticketNumber || r.id || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => ticketBadge(r.status),
    },
    { key: 'userId', label: 'User' },
    {
      key: 'updated',
      label: 'Updated',
      render: (r) => formatDate(r.updatedAt || r.updated_at || r.createdAt),
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (r) => {
        const id = r.ticketId || r.id || r._id;
        return id ? (
          <Link to={`/support-tickets/${id}`} className="btn btn-sm btn-outline-primary">
            Open
          </Link>
        ) : (
          '—'
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Support tickets" subtitle="Customer support queue" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Status</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                {TICKET_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                Filter
              </label>
              <FilterSubmitButton onClick={() => setSkip(0)} />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <DataTable columns={columns} rows={rows} rowKey={(r, i) => r.ticketId || r.id || r._id || i} />
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>
    </>
  );
}
